import {
  Box,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import strings from "global/constants/StringConstants";
import { maskedContent } from "helpers/maskedContent";
import { hasAllowedExtension } from "screens/ContractCompare/ContractCompareTypesAndValidation";
import styles from "../ContractSearch.styles";
import t from "../ContractSearch.tokens";
import { CompareLock, MatchedContract } from "../ContractSearchTypes";
import { CompareGlyph, LockGlyph } from "./ContractSearchIcons";

const copy = strings.contractCompare.page;

/** Between the two halves of a lock line, as in the copy's own "A · B" wording. */
const SEPARATOR = copy.separator;

/** The words a locked control explains itself with: the plan wording, or the administrator's own. */
const lockWords = (lock: CompareLock): string =>
  lock.kind === "plan"
    ? `${copy.lockedHint}${SEPARATOR}${copy.upgrade}`
    : lock.message;

/** The dark ink pill: "AI Assistant isn't in your plan · Upgrade", "Upgrade" underlined. */
const LockedTip = ({ lock }: { lock: CompareLock }) =>
  lock.kind === "plan" ? (
    <>
      {copy.lockedHint}
      {SEPARATOR}
      <Box component="span" sx={styles.lockedTipUpgrade}>
        {copy.upgrade}
      </Box>
    </>
  ) : (
    <>{lock.message}</>
  );

/** The tooltip chrome of the locked control. */
const lockedTipProps = { tooltip: { sx: styles.lockedTip } };

// ---------------------------------------------------------------------------------------- a source

interface CompareSourceEntryProps {
  contract: MatchedContract;
  /** Id of the element that carries the file name; it describes the control (the name is never in its label). */
  fileNameId: string;
  lock?: CompareLock | null;
  onCompare: (contract: MatchedContract) => void;
}

/**
 * Beside one matched contract: "Compare with a new version" for a PDF or Word file, otherwise a quiet hint that
 * says why not -- a source shows the control or the hint, never both (D53), by the same file-name test the compare
 * page uses. Both carry the masking marks (D41): they sit next to a file name.
 */
export const CompareSourceEntry = ({
  contract,
  fileNameId,
  lock,
  onCompare,
}: CompareSourceEntryProps) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));

  if (!hasAllowedExtension(contract.fileName)) {
    return (
      <Typography component="span" {...maskedContent} sx={styles.compareHint}>
        {copy.cannotCompare}
      </Typography>
    );
  }

  const noteId = `${fileNameId}-lock`;
  const pill = (
    <Box
      component="button"
      type="button"
      {...maskedContent}
      aria-describedby={lock ? `${fileNameId} ${noteId}` : fileNameId}
      aria-haspopup={lock?.kind === "plan" ? "dialog" : undefined}
      sx={styles.comparePill(!!lock)}
      onClick={() => onCompare(contract)}
    >
      {lock ? (
        <LockGlyph
          sx={{ width: t.comparePill.iconSize, height: t.comparePill.iconSize }}
        />
      ) : (
        <CompareGlyph
          sx={{ width: t.comparePill.iconSize, height: t.comparePill.iconSize }}
        />
      )}
      {copy.compareWithNew}
    </Box>
  );

  if (!lock) return pill;

  // Phone: no hover, so the explanation is a visible line under the pill (the 360 frame), with its own
  // "Upgrade" button. Elsewhere it is the dark pill on hover / focus.
  if (isPhone) {
    return (
      <>
        {pill}
        <Box id={noteId} sx={styles.lockNote}>
          {lock.kind === "plan" ? (
            <>
              {copy.lockedHint}
              {SEPARATOR}
              <Box
                component="button"
                type="button"
                sx={styles.lockNoteUpgrade}
                onClick={() => onCompare(contract)}
              >
                {copy.upgrade}
              </Box>
            </>
          ) : (
            lock.message
          )}
        </Box>
      </>
    );
  }

  return (
    <>
      <Tooltip
        title={<LockedTip lock={lock} />}
        placement="right"
        describeChild
        componentsProps={lockedTipProps}
      >
        {pill}
      </Tooltip>
      <Box component="span" id={noteId} sx={visuallyHidden}>
        {lockWords(lock)}
      </Box>
    </>
  );
};
